import { AirtaskerRegion } from '@airtasker/kmp-fundamental';
import { AnalyticsBrowser, User } from '@segment/analytics-next';

import { DataDogRumAgent } from 'lib/datadog/initializeDatadog';
import { getCookieWithDefaultValue, setCookie } from 'lib/utils/cookieUtils';
import { publicRuntimeConfig } from 'lib/utils/Environment';

import { AnonymousUserIdClientAdapter } from './anonymousUserId/anonymousUserIdClientAdapter';
import {
  AttributionValues,
  getAllAdsValues,
  getAllCampaignValues,
  getAllPageValues
} from './attachAttributionData';
import {
  UTM_CAMPAIGN,
  UTM_CONTENT,
  UTM_MEDIUM,
  UTM_SOURCE,
  UTM_TERM
} from './attributionProperties';

interface TrackerPageData {
  path: string;
  referrer: string;
  search: string;
  title: string;
  url: string;
}

const AU_CURRENCY_CODE = 'AUD';

// maps from cookie names to segment properties
const segmentCookieName = 'ajs_anonymous_id';
const segmentCookieDomain = 'oneflare.com';
const segmentCookieExpiryMillis = 1 * 24 * 60 * 60 * 1000; // 1 day

export interface Analytics {
  track(options: any): void;
  getCurrencyCode: () => string;
  getPageData: () => TrackerPageData;
  getTimeZone: () => string;
  getRegionCode: () => string;
  segmentPageEvent?: () => void;
}

// maps from query string params (stored on cookies) to segment properties
const CAMPAIGN_PROPERTIES_MAP = new Map<string, string>([
  [UTM_MEDIUM, 'medium'],
  [UTM_CAMPAIGN, 'name'],
  [UTM_CONTENT, 'content'],
  [UTM_TERM, 'term'],
  [UTM_SOURCE, 'source']
]);

class AnalyticsUserManager {
  private analyticsUser: User;
  private anonIdProvider: AnonymousUserIdClientAdapter;

  constructor(private tracker: AnalyticsBrowser) {
    this.anonIdProvider = new AnonymousUserIdClientAdapter();
  }

  async identify(userId?: string): Promise<void> {
    try {
      this.analyticsUser = await this.tracker.user();
      this.syncAnyonymousId();
      if (userId) {
        await this.tracker.identify(userId);
      }
    } catch (error) {
      DataDogRumAgent.addRumError(
        error,
        'Oneflare | AnalyticsUserManager | identify'
      );
    }
  }

  syncAnyonymousId() {
    let oneflareAnonId = this.anonIdProvider.getId();
    const segmentAnonId = this.getSegmentAnonId();

    if (!oneflareAnonId) {
      oneflareAnonId = this.anonIdProvider.generateId();
      console.info(
        `oneflare anonId is not available, using generated id '${oneflareAnonId}'`
      );
      this.anonIdProvider.setId(oneflareAnonId);
    }

    if (segmentAnonId !== oneflareAnonId) {
      console.debug(
        `syncing segmentAnonId '${segmentAnonId}' to oneflare anonId '${oneflareAnonId}'`
      );
      this.setAnonymousId(oneflareAnonId);
    }
  }

  private setAnonymousId(anonymousId: string) {
    if (this.isSegmentAnalyticsJsLoaded()) {
      this.tracker.setAnonymousId(anonymousId);
    } else {
      console.debug(
        'segment analytics sdk has not loaded, setting the segment cookie directly'
      );

      setCookie(
        segmentCookieName,
        JSON.stringify(anonymousId),
        new Date(Date.now() + segmentCookieExpiryMillis),
        segmentCookieDomain
      );
    }
  }

  private getSegmentAnonId(): string {
    if (this.isSegmentAnalyticsJsLoaded()) {
      return this.analyticsUser.anonymousId() ?? '';
    }
    console.debug(
      'segment analytics sdk has not loaded, accessing the segment cookie directly'
    );
    const segmentCookie = getCookieWithDefaultValue(segmentCookieName);
    try {
      return JSON.parse(segmentCookie);
    } catch (error) {
      console.info('Analytics 2.0 cookie detected');
      return segmentCookie ?? '';
    }
  }

  private isSegmentAnalyticsJsLoaded() {
    // The user function is not available before the analytics.js library is loaded
    return typeof this.analyticsUser !== 'undefined';
  }
}

interface MappedData {
  event_name?: string;
  timezone?: string;
  properties?: any[];
  conversion_event?: boolean;
}

/**
 * Oneflare Analytics class
 * This class is used to track events and page views using Segment Analytics
 *
 * See: https://segment.com/docs/connections/spec/ for more methods and options to extend this class
 */
class OneflareAnalytics implements Analytics {
  tracker: AnalyticsBrowser;
  static instance: OneflareAnalytics;
  analyticsUserManager: AnalyticsUserManager;

  constructor(userId?: string) {
    if (OneflareAnalytics.instance) {
      return OneflareAnalytics.instance;
    }

    if (typeof window == 'undefined') {
      console.error(
        'Oneflare Analytics can only be initialized on the client side'
      );
    } else {
      this.initBrowserAgent(userId);
    }
  }

  private initBrowserAgent(userId?: string) {
    this.tracker = AnalyticsBrowser.load({
      cdnURL: publicRuntimeConfig.NEXT_PUBLIC_SEGMENT_CDN_URL!,
      writeKey: publicRuntimeConfig.NEXT_PUBLIC_SEGMENT_WRITE_KEY!
    });
    this.analyticsUserManager = new AnalyticsUserManager(this.tracker);
    this.analyticsUserManager.identify(userId);
    OneflareAnalytics.instance = this;
  }

  track(mappedData: MappedData) {
    const { event_name: eventName, timezone, ...properties } = mappedData;
    let customProperties = {
      ...properties
    };

    const options: { [k: string]: any } = {};

    options.context = {
      ip: '0.0.0.0',
      timezone
    };

    options.integrations = {};

    // Add campaign properties to context if there are any ad values present
    const campaignValues = this.loadCampaignValues();
    if (campaignValues && Object.keys(campaignValues).length > 0) {
      options.context.campaign = campaignValues;
    }

    // Add ad properties to custom properties if there are any ad values present
    const adsValues = this.loadAdsValues();
    if (adsValues && Object.keys(adsValues).length > 0) {
      customProperties = {
        ...customProperties,
        ...adsValues
      };
    }

    // Add page properties to custom properties if there are any page values present
    const pageValues = this.loadPageValues();

    if (pageValues && Object.keys(pageValues).length > 0) {
      const pageProperties = {
        initial_page: {
          ...pageValues
        }
      };
      customProperties = {
        ...customProperties,
        ...pageProperties
      };
    }

    if (mappedData.conversion_event) {
      options.integrations = { All: true };
    }

    this.analyticsUserManager.syncAnyonymousId();
    try {
      this.tracker.track(eventName, customProperties, options);
    } catch (error) {
      DataDogRumAgent.addRumError(
        error,
        'Oneflare | OneflareAnalytics | track'
      );
    }
  }

  getPageData() {
    return {
      path: window.location.pathname,
      referrer: window.document.referrer,
      search: window.location.search,
      title: window.document.title,
      url: `${window.location.origin}${window.location.pathname}`
    };
  }

  getCurrencyCode() {
    return AU_CURRENCY_CODE;
  }

  getTimeZone() {
    return window.Intl.DateTimeFormat().resolvedOptions().timeZone;
  }

  getRegionCode() {
    return AirtaskerRegion.AUSTRALIA.airtaskerRegionCode;
  }

  segmentPageEvent() {
    // don't track if current page is `/get-free-quotes`
    if (window.location.pathname === '/get-free-quotes') {
      return;
    }
    this.analyticsUserManager.syncAnyonymousId();

    try {
      this.tracker.page();
    } catch (error) {
      DataDogRumAgent.addRumError(
        error,
        'Oneflare | OneflareAnalytics | segmentPageEvent'
      );
    }
  }

  private loadCampaignValues(): AttributionValues {
    console.log('loadCampaignValues');
    const campaignValues = getAllCampaignValues();
    console.log('Campaign values', campaignValues);
    return (
      campaignValues &&
      Object.keys(campaignValues).reduce<AttributionValues>((values, key) => {
        const keyName = CAMPAIGN_PROPERTIES_MAP.get(key) as string;
        return { ...values, [keyName]: campaignValues[key] };
      }, {})
    );
  }

  private loadAdsValues(): AttributionValues {
    const adsValues = getAllAdsValues();

    return (
      adsValues &&
      Object.keys(adsValues).reduce<AttributionValues>(
        (values, key) => ({ ...values, [key]: adsValues[key] }),
        {}
      )
    );
  }

  // the name of the cookies have a page_ prefix that we don't want to send to segment
  private loadPageValues(): AttributionValues {
    const pageValues = getAllPageValues();

    return (
      pageValues &&
      Object.keys(pageValues).reduce<AttributionValues>((values, key) => {
        return { ...values, [key]: pageValues[key] };
      }, {})
    );
  }
}

export { OneflareAnalytics };
